import axios from 'axios';

// Unified API base configuration
// 开发环境：本地后端；生产环境：API 专属子域名 https://api.kaufmanstrategic.vip
// 也支持通过 REACT_APP_API_URL 在打包时覆盖
const isDev = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
const API_BASE_URL = process.env.REACT_APP_API_URL || (isDev ? 'http://localhost:5000/api' : 'https://api.kaufmanstrategic.vip/api');

// Export image base URL (remove trailing /api suffix only)
// 只去掉末尾的 /api，不能用 replace('/api')，因为 URL 中可能本就含 'api.' 子域
export const getImageBaseUrl = () => {
  return API_BASE_URL.replace(/\/api\/?$/, '');
};

/** 服务端行情推送 WebSocket（与 API 同主机，路径 /ws/quotes） */
export function getQuoteStreamUrl() {
  try {
    const base = API_BASE_URL.replace(/\/api\/?$/, '');
    const u = new URL(base);
    u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
    u.pathname = '/ws/quotes';
    u.hash = '';
    u.search = '';
    return u.toString();
  } catch {
    return 'ws://localhost:5000/ws/quotes';
  }
}

// Unified image URL processing function
export const getFullImageUrl = (path, fallback = null) => {
  if (!path) return fallback;
  
  const url = typeof path === 'string' ? path.trim() : String(path).trim();
  if (!url) return fallback;
  
  // Return full URL directly
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // Relative path, concatenate with base address
  if (url.startsWith('/')) {
    return `${getImageBaseUrl()}${url}`;
  }
  
  return url || fallback;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect on login/register 401 errors - let the form handle it
    const isAuthRequest = error.config?.url?.includes('/auth/login') || 
                          error.config?.url?.includes('/auth/register');
    
    if (error.response?.status === 401 && !isAuthRequest) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

// Videos API
export const videosAPI = {
  getAll: (params) => api.get('/videos', { params }),
  getById: (id) => api.get(`/videos/${id}`),
};

// Documents API
export const documentsAPI = {
  getAll: (params) => api.get('/documents', { params }),
  getById: (id) => api.get(`/documents/${id}`),
  download: (id) => api.post(`/documents/${id}/download`),
};

// Trades API
export const tradesAPI = {
  getAll: (params) => api.get('/trades', { params }),
  getById: (id) => api.get(`/trades/${id}`),
  getStats: () => api.get('/trades/stats'),
  getStockPrice: (symbol) => api.get(`/trades/stock-price/${symbol}`),
  getStockPrices: (symbols) => api.post('/trades/stock-prices', { symbols }),
};

// Strategies API
export const strategiesAPI = {
  getAll: (params) => api.get('/strategies', { params }),
  getById: (id) => api.get(`/strategies/${id}`),
  getToday: () => api.get('/strategies/today'),
};

// Leaderboard API
export const leaderboardAPI = {
  getAll: (params) => api.get('/leaderboard', { params }),
  getTop3: (params) => api.get('/leaderboard/top3', { params }),
  getPrizes: () => api.get('/leaderboard/prizes'),
};

// VIP API
export const vipAPI = {
  getTiers: () => api.get('/vip/tiers'),
  getProgress: () => api.get('/vip/progress'),
};

// AI API - with extended timeout for AI processing
export const aiAPI = {
  stockPicker: (data) => api.post('/ai/stock-picker', data, { timeout: 120000 }),
  diagnosis: (data) => api.post('/ai/diagnosis', data, { timeout: 120000 }),
  getHistory: (params) => api.get('/ai/history', { params }),
};

// Profile API
export const profileAPI = {
  get: () => api.get('/profile'),
  getStats: () => api.get('/profile/stats'),
};

// Popup API
export const popupAPI = {
  get: () => api.get('/popup'),
};

// Likes API
export const likesAPI = {
  get: () => api.get('/likes'),
  check: () => api.get('/likes/check'),
  like: () => api.post('/likes'),
};

export default api;

